import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getRatingIntFromText } from '../../../../models/UiUtils';
import {
	invalidateGetGoogleBusinessReview,
	invalidateInfiniteReviews,
	usePostGoogleBusinessReply,
} from '../../../../queries';
import { AIAssistantThinkingImageUrl } from '../../../../web/components/ai/AIContentGenerationWizard/models';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { LocationPinIcon } from '../../../../web/components/svgs/icons/LocationPinIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { THEME } from '../../../components-v2/Theme/theme';
import { GoogleReviewReplyAssistantChat } from '../../../containers/templates/AssistantChat';
import { noteTintColor } from '../../../styles/colors';
import { Button } from '../../Button';
import { CloseButton } from '../../CloseButton';
import { ActiveGoogleBusinessReview } from '../../Happiness/DashboardGoogleBusinessProfileCard';
import { StarRating } from '../../surveys/StarRating';
import { LeftChevron } from '../../svgs/icons/LeftChevron';
import { RightChevron } from '../../svgs/icons/RightChevron';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';
import { mobileStyleSheet } from './mobileStyles';

interface GoogleBusinessReplyMobileModalProps {
	isOpen: boolean;
	activeReview?: ActiveGoogleBusinessReview;
	onCloseModal: () => void;
	isLoadingReviews: boolean;
	goToNext: () => void;
	goBack: () => void;
	onSuccess: (reviewId: string) => void;
	remainingReviewsCount: number;
	disableNext: boolean;
	disablePrevious: boolean;
	carouselProgressText: string;
}

export const GoogleBusinessReplyMobileModal = ({
	isOpen,
	activeReview,
	onCloseModal,
	isLoadingReviews,
	disableNext,
	disablePrevious,
	goToNext,
	onSuccess: onSuccessFromProps,
	remainingReviewsCount,
	goBack,
	carouselProgressText: _carouselProgressText,
}: GoogleBusinessReplyMobileModalProps) => {
	const userSession = useUserSession();
	const [response, setResponse] = useState('');
	const [showSuccessScreen, setShowSuccessScreen] = useState(false);
	const [isAiAssistantExpanded, setIsAiAssistantExpanded] = useState(false);
	const [chatSessionId, setChatSessionId] = useState<string | undefined>(undefined);

	const reviewId = activeReview?.id;

	const onSuccess = () => {
		if (reviewId) {
			onSuccessFromProps(reviewId);
			setResponse('');
			invalidateGetGoogleBusinessReview(reviewId);
			invalidateInfiniteReviews();
			setShowSuccessScreen(true);
		}
	};

	const postGoogleBusinessReplyMutation = usePostGoogleBusinessReply({
		onSuccess,
	});

	const onPostReplyClicked = () => {
		postGoogleBusinessReplyMutation.reset();
		postGoogleBusinessReplyMutation.mutate({
			comment: response,
			reviewId,
			sessionId: chatSessionId ?? activeReview?.aiGeneratedReply?.sessionId,
		});
	};

	const onCancel = () => {
		setResponse('');
		if (reviewId) {
			invalidateGetGoogleBusinessReview(reviewId);
		}
		onCloseModal();
	};

	const closeModal = () => {
		setShowSuccessScreen(false);
		onCloseModal();
	};

	const onTextChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setResponse(event.target.value || '');
	};

	// Prepopulate response with generated reply if includeGeneratedReply is true
	React.useEffect(() => {
		if (activeReview?.includeGeneratedReply && activeReview?.aiGeneratedReply?.comment) {
			setResponse(activeReview.aiGeneratedReply.comment);
		} else {
			setResponse('');
		}
	}, [activeReview?.includeGeneratedReply, activeReview?.aiGeneratedReply?.comment]);

	const getDaysAgoText = (value: any) => {
		return moment(value).fromNow();
	};

	const getInitial = (value: string) => {
		return value.substring(0, 1);
	};

	const isReplying = postGoogleBusinessReplyMutation.status === 'pending';
	const totalStars = 5;

	const renderReviewContent = () => {
		if (isLoadingReviews || !activeReview) {
			return <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='small' />;
		}

		const rating = getRatingIntFromText(activeReview.rating);
		const reviewText = activeReview.text || '';

		return (
			<>
				<div className={css(mobileStyleSheet.navigationRow)}>
					<button
						onClick={goBack}
						className={css(mobileStyleSheet.navButton, disablePrevious && baseStyleSheet.disabled)}
					>
						<LeftChevron height={24} width={24} colorFill={THEME.colors.text.light} />
					</button>
					<div className={css(mobileStyleSheet.userInfo)}>
						<div className={css(mobileStyleSheet.userInfoContent)}>
							<Avatar
								fallbackText={getInitial(activeReview.reviewer.name)}
								imgSrc={activeReview.reviewer.profilePhotoUrl}
								styles={[mobileStyleSheet.navAvatar]}
								fallbackStyles={[mobileStyleSheet.navAvatar]}
							/>
							<div className={css(mobileStyleSheet.userDetails)}>
								<span className={css(mobileStyleSheet.userName)}>{activeReview.reviewer.name}</span>
								<span className={css(mobileStyleSheet.userPostedTime)}>
									Posted {getDaysAgoText(activeReview.updateTime)}
								</span>
							</div>
						</div>
						<div className={css(mobileStyleSheet.location)}>
							<LocationPinIcon className={css(mobileStyleSheet.locationIcon)} fillColor={THEME.colors.primary.main} />
							<span>{activeReview.locationDescription}</span>
						</div>
					</div>
					<button
						onClick={goToNext}
						className={css(mobileStyleSheet.navButton, disableNext && baseStyleSheet.disabled)}
					>
						<RightChevron height={24} width={24} color={THEME.colors.text.light} />
					</button>
				</div>

				<div className={css(mobileStyleSheet.reviewSection)}>
					<div className={css(mobileStyleSheet.rating)}>
						<StarRating
							readonly
							fillColor={noteTintColor}
							total={totalStars}
							value={rating}
							starStyles={[mobileStyleSheet.ratingStar]}
						/>
					</div>
					<p className={css(mobileStyleSheet.reviewText)}>
						{reviewText || <i className={css(mobileStyleSheet.emptyComment)}>no comment added</i>}
					</p>
				</div>
				<div className={css(mobileStyleSheet.assistantContainer)}>
					{!isAiAssistantExpanded ? (
						<div className={css(mobileStyleSheet.aiToggleContainer)}>
							<button
								className={css(mobileStyleSheet.aiToggleButton)}
								onClick={() => setIsAiAssistantExpanded(!isAiAssistantExpanded)}
							>
								<span className={css(mobileStyleSheet.aiToggleIcon)}>
									<img
										className={css(mobileStyleSheet.aiToggleIconImage)}
										src={AIAssistantThinkingImageUrl}
										alt='AI Assistant Icon'
									/>
								</span>
								<span className={css(mobileStyleSheet.aiToggleText)}>Reply with AI</span>
								<span
									className={css(
										mobileStyleSheet.aiToggleChevron,
										isAiAssistantExpanded && mobileStyleSheet.aiToggleChevronExpanded
									)}
								>
									▼
								</span>
							</button>
						</div>
					) : (
						<GoogleReviewReplyAssistantChat
							key={activeReview.id}
							isOpen
							onUseGoogleReviewReply={(reply, sessionId) => {
								setResponse(reply);
								setChatSessionId(sessionId);
								setIsAiAssistantExpanded(false);
							}}
							context={{
								review: activeReview,
								companyName: userSession?.account?.companyName || '',
								reply: response,
							}}
							onClose={() => setIsAiAssistantExpanded(false)}
						/>
					)}
				</div>

				<div
					className={css(mobileStyleSheet.responseRow, isAiAssistantExpanded && mobileStyleSheet.responseRowCompact)}
				>
					{showSuccessScreen ? (
						<div className={css(mobileStyleSheet.successScreen)}>
							<h1>
								<SuccessCheckIcon /> Your Response has been posted
							</h1>
							{remainingReviewsCount ? <p>You have {remainingReviewsCount} more reviews ready for a reply</p> : null}
							<div className={css(baseStyleSheet.flex, baseStyleSheet.gap2)}>
								<Button onClick={() => setShowSuccessScreen(false)} label='See all reviews' kind='primary' />
								<Button onClick={closeModal} label='Close' kind='reverse' />
							</div>
						</div>
					) : null}

					{!showSuccessScreen ? (
						<>
							<section
								className={css(
									mobileStyleSheet.responseContainer,
									isAiAssistantExpanded && mobileStyleSheet.responseContainerCompact
								)}
							>
								<h3 className={css(mobileStyleSheet.responseTitle)}>New Response</h3>

								<textarea
									value={response}
									onChange={onTextChanged}
									rows={6}
									className={css(mobileStyleSheet.textArea)}
									placeholder='Type your response here...'
								/>

								{postGoogleBusinessReplyMutation.isError ? (
									<div className={css(mobileStyleSheet.errorContainer)}>
										There was an issue submitting your response. You can try again, or reach out to support.
									</div>
								) : null}

								{isReplying ? <LoadingSpinner type='small' /> : null}

								{!isReplying ? (
									<div className={css(mobileStyleSheet.buttonContainer)}>
										<button className={css(baseStyleSheet.ctaButton)} onClick={onPostReplyClicked} disabled={!response}>
											Post My Response
										</button>

										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancel} disabled={isReplying}>
											Cancel
										</button>
									</div>
								) : null}
							</section>
						</>
					) : null}
				</div>
			</>
		);
	};

	if (!isOpen) {
		return null;
	}

	return (
		<div className={css(mobileStyleSheet.fullPageContainer)}>
			<div className={css(mobileStyleSheet.header)}>
				<span>Google Review Response</span>
				<CloseButton
					onClick={onCancel}
					className={css(mobileStyleSheet.closeButton)}
					fillColor={THEME.colors.background.light}
				/>
			</div>
			<div className={css(mobileStyleSheet.body)}>{renderReviewContent()}</div>
		</div>
	);
};
